import { useState } from 'react'
import { AlertTriangle, CheckCircle2, XCircle, Trash2, Loader2 } from 'lucide-react'
import { api } from '../lib/api'

// One-shot cutover tool — wipes every row of customer + transactional
// data from the D1 database so the store can go live with a clean slate.
// Reviewer accounts (Apple / Google) + the walk-in POS sentinel + all
// catalog / config / ops rosters are preserved.
//
// Gated on the backend by super_admin role AND a typed confirmation
// string; the button here is disabled until the operator types the
// exact confirmation phrase, so a mis-click can't fire it.

const REQUIRED_CONFIRMATION = 'WIPE ALL TEST DATA'

interface PurgeResult {
  ok: boolean
  preserved_user_ids: string[]
  preserved_reviewer_lookup: Array<{ id: string; phone: string; role: string }>
  counts: Record<string, number>
  errors: Array<{ label: string; error: string }>
}

const WIPE_GROUPS: Array<{ title: string; items: string[] }> = [
  {
    title: 'Customer accounts + their data',
    items: [
      'Users (except reviewer accounts + walk-in sentinel)',
      'Saved addresses + saved payment methods',
      'Push tokens + biometric sessions',
      'Loyalty ledgers: drops, points, store credit',
      'Referral signups + discount codes (all codes)',
      'Reviews + AI chat history + recommendation clicks',
    ],
  },
  {
    title: 'Orders + transactions',
    items: [
      'Delivery orders + POS sales + phone-in orders',
      'Order items, add-ons, dispatch log, issues',
      'Payment transactions + auth/capture ledger',
      'Returns + refunds (pos_returns)',
      'Event ticket bookings',
      'Order share tokens + pay-link tokens + tracking share tokens',
    ],
  },
  {
    title: 'POS operations',
    items: [
      'Shifts + Z-reports',
      'Held / saved carts',
      'Cash settlements',
      'Legacy pre-unification POS sales (pos_sales table)',
    ],
  },
  {
    title: 'ERP ledgers (rebuilt at cutover)',
    items: [
      'Stock movements ledger (reset)',
      'Stock counts',
      'Purchase orders + received qty',
      'Supplier invoices + payments',
      'Gift cards + gift-card ledger',
    ],
  },
  {
    title: 'Other transactional',
    items: [
      'Phone-verification OTP records',
      'Abandoned carts + game plays',
      'Support messages + catering requests',
      'Notification logs',
      'Zoho sales export mirror',
    ],
  },
]

const PRESERVE_GROUPS: Array<{ title: string; items: string[] }> = [
  {
    title: 'Catalog',
    items: [
      'Products (rows kept; stock qty zeroed, cost_fils preserved)',
      'Categories, add-ons, product reviews from reviewers',
      'Promo shelves, promo banners, marketing site content',
    ],
  },
  {
    title: 'Config + rosters',
    items: [
      'Stores + store settings + loyalty settings',
      'Admin users + cashier users + drivers',
      'Suppliers',
      'Events + experiences + games + game prizes',
      'Accounting periods',
    ],
  },
  {
    title: 'Reviewer / test accounts (intact)',
    items: [
      'Apple / Google reviewer users + their orders, cart, prizes, addresses',
      'Walk-in POS sentinel user (`walkin-system`)',
      'Admin audit log (immutable — kept for compliance)',
    ],
  },
]

export default function DataPurge() {
  const [confirmation, setConfirmation] = useState('')
  const [submitting, setSubmitting] = useState(false)
  const [result, setResult] = useState<PurgeResult | null>(null)
  const [error, setError] = useState<string | null>(null)

  const canFire = confirmation === REQUIRED_CONFIRMATION && !submitting

  async function fire() {
    if (!canFire) return
    if (!window.confirm(`This will permanently delete every customer + transactional row in production. Type-confirm was "${confirmation}". Proceed?`)) return

    setSubmitting(true)
    setError(null)
    setResult(null)
    try {
      const res = await api.post<PurgeResult>('/system/data-purge', { confirmation })
      setResult(res.data)
      setConfirmation('')
    } catch (e) {
      const err = e as { response?: { data?: { message?: string } } }
      setError(err.response?.data?.message ?? 'Purge failed — check console + Sentry')
    } finally {
      setSubmitting(false)
    }
  }

  const totalWiped = result
    ? Object.values(result.counts).filter(n => n >= 0).reduce((a, b) => a + b, 0)
    : 0

  return (
    <div className="max-w-4xl mx-auto p-6 space-y-6">
      <div className="flex items-center gap-3">
        <div className="w-12 h-12 rounded-full bg-red-500/15 flex items-center justify-center">
          <AlertTriangle className="text-red-400" size={24} />
        </div>
        <div>
          <h1 className="text-2xl font-bold text-white">Data purge</h1>
          <p className="text-sm text-gray-400">One-shot cutover tool — wipes all test data before store go-live</p>
        </div>
      </div>

      <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-4 flex gap-3">
        <AlertTriangle className="text-red-400 shrink-0 mt-0.5" size={18} />
        <div className="text-sm text-red-200 space-y-2">
          <div><strong>This is destructive and irreversible.</strong> There is no "undo" — the deleted rows are gone the moment you fire.</div>
          <div>Use this <strong>once</strong>, right before the store's real cutover, to zero out the test data we've accumulated during development. After firing you'll enter opening-balance purchase orders to bring real stock into the system.</div>
        </div>
      </div>

      <div className="grid md:grid-cols-2 gap-4">
        <div className="bg-[#1a1a1a] border border-red-500/30 rounded-lg p-4 space-y-4">
          <h2 className="text-red-400 font-semibold flex items-center gap-2">
            <Trash2 size={16} /> Wipes
          </h2>
          {WIPE_GROUPS.map(g => (
            <div key={g.title}>
              <div className="text-xs uppercase tracking-wide text-gray-500 mb-1">{g.title}</div>
              <ul className="text-xs text-gray-300 space-y-0.5 list-disc pl-4">
                {g.items.map(i => <li key={i}>{i}</li>)}
              </ul>
            </div>
          ))}
        </div>

        <div className="bg-[#1a1a1a] border border-green-500/30 rounded-lg p-4 space-y-4">
          <h2 className="text-green-400 font-semibold flex items-center gap-2">
            <CheckCircle2 size={16} /> Preserves
          </h2>
          {PRESERVE_GROUPS.map(g => (
            <div key={g.title}>
              <div className="text-xs uppercase tracking-wide text-gray-500 mb-1">{g.title}</div>
              <ul className="text-xs text-gray-300 space-y-0.5 list-disc pl-4">
                {g.items.map(i => <li key={i}>{i}</li>)}
              </ul>
            </div>
          ))}
        </div>
      </div>

      <div className="bg-[#0f0f0f] border border-white/10 rounded-lg p-6 space-y-4">
        <label className="block">
          <div className="text-sm text-gray-300 mb-2">
            To fire, type the confirmation phrase exactly (case-sensitive):
          </div>
          <div className="text-sm font-mono text-red-400 mb-2 select-all">{REQUIRED_CONFIRMATION}</div>
          <input
            type="text"
            value={confirmation}
            onChange={e => setConfirmation(e.target.value)}
            placeholder="Type the phrase above"
            className="w-full px-4 py-2 rounded-md bg-[#1a1a1a] border border-[#3a3a3a] text-white font-mono focus:outline-none focus:border-red-500/50"
          />
        </label>
        <button
          onClick={fire}
          disabled={!canFire}
          className="w-full py-3 rounded-lg bg-red-600 text-white font-semibold flex items-center justify-center gap-2 hover:bg-red-500 disabled:opacity-40 disabled:cursor-not-allowed"
        >
          {submitting ? <Loader2 className="animate-spin" size={18} /> : <Trash2 size={18} />}
          {submitting ? 'Wiping…' : 'Purge test data'}
        </button>
        {error && <p className="text-sm text-red-400">{error}</p>}
      </div>

      {result && (
        <div className="bg-[#0f0f0f] border border-green-500/30 rounded-lg p-6 space-y-4">
          <div className="flex items-center gap-2 text-green-400 font-semibold">
            <CheckCircle2 size={18} /> Purge complete
          </div>
          <div className="grid grid-cols-3 gap-3">
            <SummaryTile label="Total rows deleted" value={totalWiped} tone="green" />
            <SummaryTile label="Preserved user IDs" value={result.preserved_user_ids.length} tone="neutral" />
            <SummaryTile label="Errors" value={result.errors.length} tone={result.errors.length > 0 ? 'red' : 'neutral'} />
          </div>

          {result.preserved_reviewer_lookup.length > 0 && (
            <div className="bg-[#1a1a1a] border border-white/10 rounded-md p-3">
              <div className="text-xs uppercase tracking-wide text-gray-500 mb-2">Reviewer users preserved</div>
              {result.preserved_reviewer_lookup.map(r => (
                <div key={r.id} className="text-xs text-gray-300 font-mono">
                  {r.role}: {r.phone} → <code className="text-gray-400">{r.id}</code>
                </div>
              ))}
            </div>
          )}

          <div className="bg-[#1a1a1a] border border-white/10 rounded-md overflow-hidden">
            <div className="px-4 py-2 border-b border-white/10 text-xs uppercase tracking-wide text-gray-500">Per-table counts</div>
            <div className="max-h-96 overflow-y-auto">
              <table className="w-full text-sm">
                <tbody>
                  {Object.entries(result.counts).map(([table, count]) => (
                    <tr key={table} className="border-t border-white/5">
                      <td className="px-4 py-1.5 text-gray-400 font-mono text-xs">{table}</td>
                      <td className="px-4 py-1.5 text-right text-white font-mono text-xs">
                        {count === -1 ? <span className="text-red-400">error</span> : count.toLocaleString()}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {result.errors.length > 0 && (
            <div className="bg-red-500/10 border border-red-500/30 rounded-md p-3">
              <div className="text-xs uppercase tracking-wide text-red-400 mb-2 flex items-center gap-1">
                <XCircle size={12} /> Errors during purge
              </div>
              {result.errors.map((e, i) => (
                <div key={i} className="text-xs text-red-200 font-mono">
                  <strong>{e.label}:</strong> {e.error}
                </div>
              ))}
              <div className="text-[11px] text-red-300 mt-2">
                Some table deletes errored — likely FK violations. Fix the cascade order or drop the offending rows manually, then re-fire.
              </div>
            </div>
          )}

          <div className="text-xs text-gray-500 pt-2 border-t border-white/10">
            Next steps: (1) update pricing from Excel · (2) create opening-balance purchase orders per real supplier · (3) receive those POs so `products.stock` populates + WAC rebuilds · (4) flip JoFotara out of test mode.
          </div>
        </div>
      )}
    </div>
  )
}

function SummaryTile({ label, value, tone }: { label: string; value: number; tone: 'green' | 'red' | 'neutral' }) {
  const toneClass = tone === 'green' ? 'text-green-400' : tone === 'red' ? 'text-red-400' : 'text-gray-300'
  return (
    <div className="bg-[#1a1a1a] border border-white/10 rounded-md p-3">
      <div className="text-[10px] uppercase tracking-wide text-gray-500">{label}</div>
      <div className={`text-2xl font-mono font-bold mt-1 ${toneClass}`}>{value.toLocaleString()}</div>
    </div>
  )
}
